import { MeasurementPlanChatPayloadSchema } from '@server/schema';
import { useMutation } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, ExternalLink, FileText, LoaderCircle, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { MeasurementPlanChatPayload, MeasurementPlanEvent, PropertyChange } from '@/measurement-plan/types';

// ─── Styles ───

// Colors and labels aligned with PlanEventCard (the detail-page component).
// We intentionally keep border-l-2 here (vs border-l-4 in PlanEventCard) for
// the denser inline-chat layout. Only the colors need to match.
const CHANGE_TYPE_STYLES: Record<string, { badge: string; border: string; label: string }> = {
  add: { badge: 'bg-emerald-100 text-emerald-700', border: 'border-l-2 border-l-emerald-500', label: 'ADD' },
  modify: { badge: 'bg-amber-100 text-amber-700', border: 'border-l-2 border-l-amber-500', label: 'MODIFY' },
  remove: { badge: 'bg-rose-100 text-rose-700', border: 'border-l-2 border-l-rose-500', label: 'REMOVE' },
  already_available: {
    badge: 'bg-slate-100 text-slate-700',
    border: 'border-l-2 border-l-slate-400',
    label: 'AVAILABLE',
  },
};

// Rose instead of red to match PlanEventCard's PROP_ROW_TINT.
const PROP_STATUS_STYLES: Record<string, { row: string; border: string; label: string; text: string }> = {
  added: {
    row: 'bg-emerald-50',
    border: 'border-l-2 border-l-emerald-500',
    label: 'bg-emerald-100 text-emerald-700',
    text: 'added',
  },
  modified: {
    row: 'bg-amber-50',
    border: 'border-l-2 border-l-amber-500',
    label: 'bg-amber-100 text-amber-700',
    text: 'modified',
  },
  removed: {
    row: 'bg-rose-50',
    border: 'border-l-2 border-l-rose-400',
    label: 'bg-rose-100 text-rose-700',
    text: 'removed',
  },
};

// ─── Parsing ───

function parsePayload(
  content: string,
): { ok: true; payload: MeasurementPlanChatPayload } | { ok: false; error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(content);
  } catch {
    return { ok: false, error: 'Malformed JSON' };
  }
  const result = MeasurementPlanChatPayloadSchema.safeParse(raw);
  if (!result.success) {
    return {
      ok: false,
      error: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
    };
  }
  return { ok: true, payload: result.data as MeasurementPlanChatPayload };
}

// ─── Compact event row ───
//
// This is the compact inline version for chat history. It intentionally does
// NOT reuse PlanEventCard (the detail-page component) because chat needs a
// denser layout: no Card chrome, properties as a tight inline list, narrower
// typography. PlanEventCard is the authoritative renderer for the plan detail
// page; this component is the authoritative renderer for chat bubbles.

const MAX_EXPANDED_EVENTS = 5;

interface PlanEventRowProps {
  event: MeasurementPlanEvent;
}

function PlanEventRow({ event }: PlanEventRowProps): React.ReactElement {
  const startsExpanded = event.change_type !== 'already_available';
  const [expanded, setExpanded] = useState(startsExpanded);
  const styles = CHANGE_TYPE_STYLES[event.change_type] ?? CHANGE_TYPE_STYLES.already_available!;

  const propCount = event.properties?.length ?? 0;
  const contentId = `plan-event-detail-${event.id}`;

  return (
    <div
      className={`rounded-md border border-border bg-card ${styles.border}`}
      data-testid={`plan-event-row-${event.id}`}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        onClick={() => setExpanded((p) => !p)}
        aria-expanded={expanded}
        aria-controls={contentId}
      >
        <ChevronRight
          size={12}
          className={`shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider uppercase ${styles.badge}`}
          data-testid={`plan-event-change-badge-${event.id}`}
        >
          {styles.label}
        </span>
        <span className="truncate font-mono text-xs font-medium">{event.name}</span>
        {propCount > 0 && (
          <span className="ml-auto shrink-0 text-[0.6875rem] text-muted-foreground">
            {propCount} {propCount === 1 ? 'prop' : 'props'}
          </span>
        )}
      </button>

      {expanded && (
        <div id={contentId} className="border-t border-border px-3 py-2 text-xs">
          {event.description && <div className="mb-2 text-muted-foreground">{event.description}</div>}
          {event.properties && event.properties.length > 0 && (
            <ul className="space-y-1">
              {event.properties.map((p: PropertyChange) => {
                const propStyle = PROP_STATUS_STYLES[p.status];
                return (
                  <li
                    key={p.name}
                    className={`flex items-center gap-2 rounded-sm px-2 py-1 ${propStyle?.row ?? ''} ${propStyle?.border ?? ''}`}
                  >
                    <span className="font-mono">{p.name}</span>
                    <span className="text-muted-foreground">{p.type}</span>
                    {propStyle && (
                      <span
                        className={`ml-auto rounded-full px-1.5 py-0.5 text-[0.5625rem] font-semibold tracking-wider uppercase ${propStyle.label}`}
                      >
                        {propStyle.text}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {(!event.properties || event.properties.length === 0) && event.change_type === 'remove' && (
            <div className="text-muted-foreground italic">No properties — event is being removed.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Props ───

interface MeasurementPlanBlockProps {
  content: string;
}

// ─── Main component ───

/**
 * Save button + inline error state, extracted so that `useOrpc` (which
 * internally requires a WorkspaceProvider) is only called when the parent
 * block knows a workspace is present. The parent uses useWorkspaceOrNull
 * and gates the render of this subcomponent on `ws !== null`, which is
 * how we keep MeasurementPlanBlock renderable in a workspace-less test
 * fixture without breaking the hooks rules.
 */
function DirectSaveButton({
  payload,
  onSaved,
}: {
  payload: MeasurementPlanChatPayload;
  onSaved: (planId: string) => void;
}): React.ReactElement {
  const orpc = useOrpc();
  const [error, setError] = useState<string | null>(null);
  const saveMutation = useMutation(
    orpc.measurementPlan.save.mutationOptions({
      onSuccess: (result) => {
        setError(null);
        onSaved(result.plan.id);
      },
      onError: (err: Error) => setError(err.message || 'Failed to save plan'),
    }),
  );

  function handleClick(): void {
    // The chat artifact uses the looser MeasurementPlanSpecPreviewSchema,
    // which allows sources[].name, sources[].type, metadata.generated_at,
    // and metadata.agent_model to be missing. The server save procedure
    // requires the strict schema, so fill in safe defaults before
    // submitting. The instrumentation agent re-derives types from the
    // cloned repo anyway, so placeholder `type` values are harmless.
    const { spec, title, description, businessContext } = payload;
    const strictSpec = {
      ...spec,
      sources: spec.sources.map((s) => ({
        source_id: s.source_id,
        name: s.name ?? s.source_id,
        type: s.type ?? 'unknown',
      })),
      metadata: {
        generated_at: spec.metadata?.generated_at ?? new Date().toISOString(),
        agent_model: spec.metadata?.agent_model ?? 'chat-block-direct-save',
        ...(spec.metadata?.baseline_doc_ids ? { baseline_doc_ids: spec.metadata.baseline_doc_ids } : {}),
      },
    };
    saveMutation.mutate({
      title,
      ...(description ? { description } : {}),
      ...(businessContext ? { businessContext } : {}),
      spec: strictSpec,
    });
  }

  return (
    <>
      {error && (
        <span className="text-xs text-rose-600" data-testid="plan-save-error">
          {error}
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={saveMutation.isPending}
        data-testid="plan-save-button"
      >
        {saveMutation.isPending ? (
          <LoaderCircle size={12} className="mr-1 animate-spin" />
        ) : (
          <Save size={12} className="mr-1" />
        )}
        {saveMutation.isPending ? 'Saving…' : 'Save as draft'}
      </Button>
    </>
  );
}

export function MeasurementPlanBlock({ content }: MeasurementPlanBlockProps): React.ReactElement {
  const parsed = useMemo(() => parsePayload(content), [content]);
  const [showAllEvents, setShowAllEvents] = useState(false);
  // Client-side override for the saved state after the user clicks the direct
  // Save button. The chat message content is immutable in the DB, so we track
  // "saved via button" locally and merge it with the payload's isSaved below.
  const [clientSavedPlanId, setClientSavedPlanId] = useState<string | null>(null);
  const ws = useWorkspaceOrNull();

  if (!parsed.ok) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        data-testid="plan-parse-error"
      >
        Could not render measurement plan — {parsed.error}
      </div>
    );
  }

  const { payload } = parsed;
  const { spec, planId, isSaved, title, description, businessContext, summary, warnings } = payload;
  const events = spec.events ?? [];

  // Merge "saved via agent tool call" with "saved via direct Save button".
  const effectivePlanId = planId ?? clientSavedPlanId;
  const effectiveIsSaved = isSaved || clientSavedPlanId !== null;

  const visibleEvents = showAllEvents ? events : events.slice(0, MAX_EXPANDED_EVENTS);
  const hiddenCount = events.length - visibleEvents.length;
  const canShowViewLink = effectiveIsSaved && effectivePlanId !== null && ws !== null;
  // Only offer the direct-save button when we have a workspace AND there's
  // actually something to save. An empty-events draft is never worth saving.
  const canSaveDirectly = !effectiveIsSaved && ws !== null && events.length > 0;

  // Format summary: omit zero-count alreadyAvailable to reduce noise.
  const summaryParts = [
    `${summary.added} new`,
    `${summary.modified} modified`,
    `${summary.removed} removed`,
    ...(summary.alreadyAvailable > 0 ? [`${summary.alreadyAvailable} existing`] : []),
  ];

  return (
    <div
      className="overflow-hidden rounded-xl border border-border bg-card text-sm"
      data-testid="measurement-plan-block"
    >
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-start gap-2">
          <FileText size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold" data-testid="measurement-plan-title">
                {title}
              </span>
              {effectiveIsSaved && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[0.625rem] font-semibold tracking-wider text-emerald-700 uppercase"
                  data-testid="plan-saved-badge"
                >
                  <Check size={10} aria-hidden="true" />
                  Saved
                </span>
              )}
            </div>
            {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
            {businessContext && <p className="mt-1 text-xs text-muted-foreground">{businessContext}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.6875rem] text-muted-foreground">
              <span data-testid="measurement-plan-sources">
                Sources: {spec.sources.map((s) => s.name).join(', ') || 'none'}
              </span>
              <span data-testid="measurement-plan-summary">{summaryParts.join(', ')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div
          className="mx-4 mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800"
          data-testid="plan-warnings"
        >
          <div className="mb-1 text-[0.625rem] font-semibold tracking-wider uppercase">
            Warnings ({warnings.length})
          </div>
          <ul className="list-disc space-y-0.5 pl-4">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Event list */}
      <div className="space-y-2 px-4 py-3">
        {events.length === 0 && (
          <div className="text-xs text-muted-foreground italic" data-testid="plan-empty-state">
            No event changes yet — the agent is still drafting.
          </div>
        )}
        {visibleEvents.map((event) => (
          <PlanEventRow key={event.id} event={event} />
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAllEvents(true)}
            className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            data-testid="plan-events-more"
          >
            <ChevronDown size={12} />
            and {hiddenCount} more {hiddenCount === 1 ? 'change' : 'changes'}…
          </button>
        )}
      </div>

      {/* Footer actions: direct Save button (when unsaved) and/or the View
          Full Plan link (when saved). Both conditions require a workspace. */}
      {(canSaveDirectly || canShowViewLink) && (
        <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-4 py-2">
          {canSaveDirectly && <DirectSaveButton payload={payload} onSaved={(id) => setClientSavedPlanId(id)} />}
          {canShowViewLink && effectivePlanId !== null && (
            <Link
              to={ws.wp(`/plans/${effectivePlanId}`)}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-background"
              data-testid="plan-view-link"
            >
              View Full Plan
              <ExternalLink size={12} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
